import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _startsWith from 'lodash/startsWith';
//Local files
import { HeaderButton, Header, Root, Wrapper } from './TemplateBuilder.styled';
import TemplateDrawer from 'components/Template/Drawer/Drawer';
import { ReactComponent as MoreVertIcon } from 'assets/icons/vert.svg';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import DocBuilder from 'components/Documents/Builder/Builder';
import useApp from 'hooks/useApp';
import useFillTemplates from 'hooks/useFillTemplates';
import useEsignTemplates from 'hooks/useEsignTemplates';
import useTemplates from 'hooks/useTemplates';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import { DELETE_FILL_TEMPLATE, DELETE_ESIGN_TEMPLATE, DELETE_TEMPLATE } from 'helpers/confirmations';

const TemplateBuilder = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { fill_template_id, esign_template_id, template_id } = useParams();
  const { openDeleteDialog } = useApp();
  const { getFillTemplate, updateFillTemplate } = useFillTemplates();
  const { getEsignTemplate, updateEsignTemplate } = useEsignTemplates();
  const { getTemplate, updateTemplate } = useTemplates();
  const { setSuccess } = useSuccess();
  const { id, template, processing } = useCustomSelector(state => ({ 
    id: template_id ? state.templates.template.id : fill_template_id ? state.fillTemplates.fillTemplate.id : state.esignTemplates.esignTemplate.id,
    template: template_id ? state.templates.template : fill_template_id ? state.fillTemplates.fillTemplate.template : state.esignTemplates.esignTemplate.template,
    processing: template_id ? state.templates.loading : fill_template_id ? state.fillTemplates.loading.fillTemplate : state.esignTemplates.loading.esignTemplate
  }));
  const [menuanchorEl, setMenuAnchorEl] = useState(null);
  const [elements, setElements] = useState({ value: [], formatedValue: [] });
  const [file, setFile] = useState(null);
  const [templateDrawer, setTemplateDrawer] = useState({ open: false })
  const templateType = useMemo(() => pathname?.split('/')[1].replace(/s$/, ''), [pathname]);
  const builderProps = useMemo(() => ({
    'template': {
      templateId: template_id,
      get: getTemplate,
      update: updateTemplate,
      delete: DELETE_TEMPLATE,
      flow: ''
    },
    'fill_template': {
      templateId: fill_template_id,
      get: getFillTemplate,
      update: updateFillTemplate,
      delete: DELETE_FILL_TEMPLATE,
      flow: 'fl99f'
    },
    'esign_template': {
      templateId: esign_template_id,
      get: getEsignTemplate,
      update: updateEsignTemplate,
      delete: DELETE_ESIGN_TEMPLATE,
      flow: 'fl10e'
    },
  }), [fill_template_id, esign_template_id, template_id, getTemplate, getFillTemplate, getEsignTemplate, updateTemplate, updateFillTemplate, updateEsignTemplate]);

  const openMenu = (event) => setMenuAnchorEl(event.currentTarget);
  const closeMenu = () => setMenuAnchorEl(null);
  const fetchTemplate = useCallback((id) => { 
    builderProps[templateType]?.get(id)
    .then(({ payload, error }) => {
      if (!error) {
        const file = payload?.template?.file || payload?.[templateType]?.template?.file;

        setFile(file);
      }
    })
  }, [templateType, builderProps]);
  const handleDelete = () => {
    closeMenu();
    openDeleteDialog({ id, type: builderProps[templateType]?.delete });
  }
  const handleSave = () => {
    const elements_attributes = _map(elements.value, (w) => ({ ..._startsWith(w.id, 'new_') ? { ..._omit(w, 'id', 'page') } : { ...w }, page_id: w.page.id }));
    const template = {
      id,
      ...template_id ? { elements_attributes } : { template_attributes: { elements_attributes } }
    };

    builderProps[templateType]?.update(template)
    .then(({ error }) => {
      if (!error) {
        setSuccess('Document successfully updated');
        navigate(template_id ? '/flows_list' : `/flow/${builderProps[templateType]?.flow}`);
      }
    })
  };
  const handleBack = () => {
    setFile(null);
    navigate(template_id ? '/flows_list' : `/flow/${builderProps[templateType]?.flow}`);
  };

  useEffect(() => {
    if (builderProps[templateType]?.templateId) {
      fetchTemplate(builderProps[templateType]?.templateId);
    }
  }, [builderProps, templateType, fetchTemplate]);
  useEffect(() => {
    if (!!template?.pages?.length) {
      setElements({
        value: _map(template?.elements, (w) => ({ ..._omit(w, 'elementable'), elementable_attributes: w.elementable })),
        formatedValue: template?.elements
      });
    }
  }, [template]);

  return (
    <Root>
      <TemplateDrawer open={templateDrawer.open} onClose={() => setTemplateDrawer({ open: false })} />
      <Menu
        anchorEl={menuanchorEl}
        open={!!menuanchorEl}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleDelete} disabled={processing}>Delete</MenuItem>
      </Menu>
      <Header>
        <HeaderButton disabled={processing} onClick={handleBack}>Back</HeaderButton>
        {<Box>
          <HeaderButton disabled={processing} onClick={() => setTemplateDrawer({ open: true })}>Edit</HeaderButton>
          <HeaderButton disabled={processing} onClick={handleSave}>Save</HeaderButton>
          <HeaderButton sx={{ fontWeight: 500 }} disabled={processing} onClick={openMenu}><MoreVertIcon /></HeaderButton>
        </Box>}
        
      </Header>
      <Wrapper>
        <ErrorWrapper 
          children={<DocBuilder 
            file={file}
            elements={elements} 
            pages={template?.pages}
            setElements={setElements}
          />} 
        />
      </Wrapper>
    </Root>
  );
}

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <TemplateBuilder {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);